/**
 * StandaloneWaiverPage — walk-up waiver signing without a booking.
 * Identical flow to MobileWaiverWizard (email OTP / SMS OTP / ID fallback).
 * Differences from MobileWaiverWizard:
 *   - No trip/booking object — uses org data
 *   - No PIN (or generates random 3-digit stub)
 *   - Submits to /v2/public/org/{orgSlug}/sign-waiver
 *   - Full page layout (not a modal overlay)
 */
import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Check, ChevronLeft, Shield, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import api from '../utils/api';

// ── Constants (same as MobileWaiverWizard) ────────────────────────────────────
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 20;
const MIN_NAME_LENGTH = 2;

const sanitizeInput = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').slice(0, maxLength);
};
const sanitizeInputFinal = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').trim().slice(0, maxLength);
};
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email && re.test(email) && email.length <= MAX_EMAIL_LENGTH;
};

// ── 6-digit OTP input (identical to MobileWaiverWizard) ──────────────────────
const CodeInput = ({ value, onChange, disabled = false, onComplete }) => {
  const inputRefs = useRef([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);
  const handleChange = (index, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits]; newDigits[index] = digit;
    const newValue = newDigits.join(''); onChange(newValue);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (newValue.length === 6 && onComplete) setTimeout(() => onComplete(newValue), 100);
  };
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6 && onComplete) setTimeout(() => onComplete(pasted), 100);
  };
  return (
    <div className="flex gap-2 justify-center mb-6">
      {[0,1,2,3,4,5].map(i => (
        <input key={i} ref={el => inputRefs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={digits[i] || ''} onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste}
          disabled={disabled} autoFocus={i === 0}
          className="w-12 h-14 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function StandaloneWaiverPage() {
  const { orgSlug } = useParams();

  // Org data
  const [org, setOrg] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Wizard state — mirrors MobileWaiverWizard exactly
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const signaturePadRef = useRef(null);
  const [capturedSignatureData, setCapturedSignatureData] = useState(null);
  const [hasAgreedToWaiver, setHasAgreedToWaiver] = useState(false);
  const [showWaiverText, setShowWaiverText] = useState(false);
  const [waiverViewed, setWaiverViewed] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [skippedVerification, setSkippedVerification] = useState(false);
  const [lastSendTime, setLastSendTime] = useState(0);
  const [failedMethods, setFailedMethods] = useState([]);
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const latestSmsCodeRef = useRef('');
  const latestEmailCodeRef = useRef('');

  // Load org waiver config
  useEffect(() => {
    api.get(`/v2/public/org/${orgSlug}/waiver`)
      .then(r => { setOrg(r.data); setLoadingOrg(false); })
      .catch(() => { setLoadError('Waiver not available or not configured for this organisation.'); setLoadingOrg(false); });
  }, [orgSlug]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  // Success countdown
  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [showSuccess, countdown]);

  const brandColor = org?.primaryColor || '#2563EB';

  // Validation
  const validateName = (v) => {
    const s = sanitizeInput(v, MAX_NAME_LENGTH);
    if (s.length < MIN_NAME_LENGTH) { setNameError('Name must be at least 2 characters'); return false; }
    if (!/^[a-zA-Z\s\-'.]+$/.test(s)) { setNameError('Name contains invalid characters'); return false; }
    setNameError(''); return true;
  };
  const validateEmail = (v) => {
    if (!isValidEmail(v)) { setEmailError('Please enter a valid email'); return false; }
    setEmailError(''); return true;
  };

  // Capture signature (same compression as MobileWaiverWizard)
  const captureSignatureLightweight = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return null;
    try {
      const canvas = signaturePadRef.current.getCanvas();
      const maxW = 300, maxH = 150;
      let w = canvas.width, h = canvas.height;
      if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
      if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
      const c2 = document.createElement('canvas'); c2.width = w; c2.height = h;
      const ctx = c2.getContext('2d');
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(canvas, 0, 0, w, h);
      return c2.toDataURL('image/jpeg', 0.6);
    } catch { return null; }
  };

  // Send OTP (identical logic to MobileWaiverWizard)
  const handleSendCode = async () => {
    const now = Date.now();
    if ((now - lastSendTime) / 1000 < 60 && lastSendTime > 0) {
      toast.error(`Please wait ${Math.ceil(60 - (now - lastSendTime) / 1000)}s before resending`); return;
    }
    setSendingCode(true); setVerificationFailed(false);
    try {
      if (verificationMethod === 'phone' || verificationMethod === 'sms') {
        const formatted = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber}`;
        await api.post('/public/sms/send-code', { phoneNumber: formatted });
        toast.success('SMS code sent!');
        setPhoneNumber(formatted);
      } else if (verificationMethod === 'email') {
        if (!validateEmail(email)) { setSendingCode(false); return; }
        await api.post('/public/email/send-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH) });
        toast.success('Email code sent! Check your inbox.');
      }
      setResendCooldown(60); setLastSendTime(now); setSkippedVerification(false); setStep(5);
    } catch {
      setVerificationFailed(true);
      toast.error(`Failed to send ${verificationMethod === 'email' ? 'email' : 'SMS'} code`);
    } finally { setSendingCode(false); }
  };

  // Sign waiver — verify OTP then submit
  const handleSign = async () => {
    setSigning(true);
    try {
      const currentSmsCode = latestSmsCodeRef.current || smsCode;
      const currentEmailCode = latestEmailCodeRef.current || emailCode;
      let verified = false; let verifiedContact = '';

      if ((verificationMethod === 'phone' || verificationMethod === 'sms') && phoneNumber && currentSmsCode) {
        const res = await api.post('/public/sms/verify-code', { phoneNumber, code: currentSmsCode });
        verified = res.data.valid; verifiedContact = phoneNumber;
        if (!verified) { toast.error('Invalid SMS code'); setSigning(false); return; }
      } else if (verificationMethod === 'email' && email && currentEmailCode) {
        const res = await api.post('/public/email/verify-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH), code: currentEmailCode });
        verified = res.data.valid; verifiedContact = email;
        if (!verified) { toast.error('Invalid email code'); setSigning(false); return; }
      } else if (verificationMethod === 'id') {
        verified = true; verifiedContact = 'ID Uploaded';
      }

      const safe = fn => { try { return fn(); } catch { return 'Unknown'; } };
      const deviceInfo = {
        userAgent: safe(() => navigator.userAgent),
        platform: safe(() => navigator.platform),
        screenResolution: safe(() => `${window.screen.width}x${window.screen.height}`),
        timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
        language: safe(() => navigator.language),
      };

      await api.post(`/v2/public/org/${orgSlug}/sign-waiver`, {
        signerName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '',
        verified,
        verificationMethod: verificationMethod === 'phone' ? 'sms' : verificationMethod,
        verifiedContact,
        phoneNumber: (verificationMethod === 'sms' || verificationMethod === 'phone') ? phoneNumber : '',
        email: verificationMethod === 'email' ? sanitizeInputFinal(email, MAX_EMAIL_LENGTH) : '',
        deviceInfo,
        signedAt: new Date().toISOString(),
        waiverVersion: org?.waiverVersion || '1.0',
      });

      setShowSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 400) toast.error(`Error: ${detail || 'Please check your information.'}`);
      else if (status === 404) toast.error('Organisation not found. Please refresh.');
      else toast.error('Failed to sign waiver. Please try again.');
    } finally { setSigning(false); }
  };

  // ID verification sign (no OTP needed)
  const handleIdVerificationSign = async () => {
    setSigning(true);
    try {
      const safe = fn => { try { return fn(); } catch { return 'Unknown'; } };
      const deviceInfo = {
        userAgent: safe(() => navigator.userAgent),
        platform: safe(() => navigator.platform),
        screenResolution: safe(() => `${window.screen.width}x${window.screen.height}`),
        timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      };
      await api.post(`/v2/public/org/${orgSlug}/sign-waiver`, {
        signerName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '',
        verified: false,
        verificationMethod: 'id',
        verifiedContact: 'ID Required',
        phoneNumber: phoneNumber || '',
        email: email || '',
        deviceInfo,
        idVerificationRequired: true,
        signedAt: new Date().toISOString(),
        waiverVersion: org?.waiverVersion || '1.0',
      });
      setShowSuccess(true);
    } catch { toast.error('Failed to sign waiver. Please try again.'); }
    finally { setSigning(false); }
  };

  // ── Loading / Error states ──────────────────────────────────────────────────
  if (loadingOrg) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (loadError) return (
    <div className="min-h-screen flex items-center justify-center p-4 text-center">
      <div className="max-w-sm">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-700 font-medium mb-1">Waiver not available</p>
        <p className="text-gray-500 text-sm mb-4">{loadError}</p>
        <Link to="/" className="text-sm text-blue-600 hover:underline">← BookingFlows</Link>
      </div>
    </div>
  );

  // ── Success Screen ──────────────────────────────────────────────────────────
  if (showSuccess) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle2 className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Waiver Signed!</h2>
      <p className="text-gray-600 mb-2">You have successfully signed the waiver.</p>
      <p className="text-sm text-gray-500 mb-8">Thank you, {name}.</p>
      {verificationMethod === 'id' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left max-w-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">ID Required at Check-in</p>
              <p className="text-xs text-amber-700 mt-1">Please bring a valid government-issued ID for verification.</p>
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-400">This page will close automatically</p>
    </div>
  );

  // ── Main wizard ─────────────────────────────────────────────────────────────
  const progress = Math.min(step, 5) / 5;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Branded header */}
      <div className="py-4 px-4 sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org.logoUrl
              ? <img src={org.logoUrl} alt={org.orgName} className="h-8 w-auto max-w-[120px] object-contain rounded" />
              : <span className="font-semibold text-gray-900" style={{ color: brandColor }}>{org.orgName}</span>
            }
          </div>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">v{org.waiverVersion}</span>
        </div>
        {/* Progress bar */}
        <div className="max-w-md mx-auto mt-3">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress * 100}%`, background: brandColor }} />
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 pb-16 overflow-y-auto">
        <div className="max-w-md mx-auto">

          {/* STEP 1: Waiver Agreement ─────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Liability Waiver</h2>
              </div>
              {!showWaiverText ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Please Review the Waiver</h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    By proceeding, you acknowledge that we collect your signature and device information for legal enforceability.
                  </p>
                  <Button onClick={() => { setShowWaiverText(true); setWaiverViewed(true); }}
                    className="w-full h-12 text-white text-base font-semibold" style={{ background: brandColor }}>
                    I Agree &amp; View Waiver
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 leading-relaxed mb-4 max-h-72 overflow-y-auto whitespace-pre-wrap break-words">
                    {org.waiverText}
                  </div>
                  <label className="flex items-start gap-3 mb-4 cursor-pointer group p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                    <input type="checkbox" checked={hasAgreedToWaiver} onChange={e => setHasAgreedToWaiver(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I have read and agree to the <span className="font-semibold">Liability Waiver &amp; Assumption of Risk</span>
                    </span>
                  </label>
                  <Button onClick={() => setStep(2)} disabled={!hasAgreedToWaiver}
                    className="w-full h-12 text-white text-base font-semibold disabled:opacity-50"
                    style={{ background: hasAgreedToWaiver ? brandColor : '#9CA3AF' }}>
                    I Agree to the Liability Waiver
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Name & Signature ─────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Your Name &amp; Signature</h2>
              <p className="text-sm text-gray-600 mb-4">Draw your signature and type your full legal name</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Signature <span className="text-red-500">*</span></label>
                <div className="border border-gray-300 rounded-xl bg-white overflow-hidden">
                  <SignatureCanvas ref={signaturePadRef}
                    canvasProps={{ className: 'w-full h-28', style: { touchAction: 'none' } }} />
                </div>
                <button onClick={() => signaturePadRef.current?.clear()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                  Clear Signature
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Legal Name <span className="text-red-500">*</span>
                </label>
                <Input value={name} onChange={e => {
                  const s = sanitizeInput(e.target.value, MAX_NAME_LENGTH); setName(s);
                  if (s.length >= MIN_NAME_LENGTH) validateName(s);
                }} onBlur={() => name && validateName(name)}
                  placeholder="Enter your full name"
                  className={`h-12 text-base ${nameError ? 'border-red-500' : ''}`}
                  maxLength={MAX_NAME_LENGTH} />
                {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
                <p className="mt-1 text-xs text-gray-400">{name.length}/{MAX_NAME_LENGTH} characters</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={() => {
                  if (validateName(name)) {
                    setCapturedSignatureData(captureSignatureLightweight());
                    setVerificationMethod(''); setFailedMethods([]); setStep(3);
                  }
                }} disabled={name.trim().length < MIN_NAME_LENGTH}
                  className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50"
                  style={{ background: brandColor }}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Verification Method ─────────────────────────────────── */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Identity</h2>
              <p className="text-sm text-gray-600 mb-4">Choose how you'd like to receive your verification code</p>

              {/* Method selection */}
              {!verificationMethod && (
                <div className="space-y-3 mb-4">
                  {[{ method: 'email', label: 'Email', sub: 'Send code to your email', color: 'blue',
                      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> },
                     { method: 'phone', label: 'Phone (SMS)', sub: 'Send code via text message', color: 'green',
                       icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /> }
                  ].map(({ method, label, sub, color, icon }) => (
                    <button key={method} onClick={() => setVerificationMethod(method)}
                      className={`w-full p-4 border border-gray-200 rounded-xl hover:border-${color}-300 hover:bg-${color}-50 transition-all flex items-center gap-3`}>
                      <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center`}>
                        <svg className={`w-5 h-5 text-${color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">{icon}</svg>
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">{label}</div>
                        <div className="text-sm text-gray-500">{sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Email input */}
              {verificationMethod === 'email' && (<>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                  <Input type="email" value={email} onChange={e => { setEmail(sanitizeInput(e.target.value.toLowerCase(), MAX_EMAIL_LENGTH)); setVerificationFailed(false); if (emailError) setEmailError(''); }}
                    onBlur={() => email && validateEmail(email)} placeholder="your@email.com"
                    className={`h-12 text-base ${emailError ? 'border-red-500' : ''}`} maxLength={MAX_EMAIL_LENGTH} autoComplete="email" />
                  {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
                </div>
                {verificationFailed && <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4"><p className="text-sm text-red-900 font-medium">Failed to send code</p><p className="text-xs text-red-700 mt-1">Check your email or try phone below</p></div>}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('phone') && <button onClick={() => { setVerificationMethod('phone'); setVerificationFailed(false); setFailedMethods([...failedMethods, 'email']); }} className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">Can't receive email? Try phone instead</button>}
                  {failedMethods.includes('phone') && <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }} className="w-full text-sm py-2.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">Can't receive verification? Verify with ID instead</button>}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setVerificationMethod(''); setVerificationFailed(false); }} className="w-20 h-12 border-gray-200"><ChevronLeft className="w-5 h-5" /></Button>
                  <Button onClick={handleSendCode} disabled={sendingCode || !isValidEmail(email)} className="flex-1 h-12 text-white font-semibold disabled:opacity-50" style={{ background: brandColor }}>
                    {sendingCode ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span> : 'Send Code'}
                  </Button>
                </div>
              </>)}

              {/* Phone input */}
              {verificationMethod === 'phone' && (<>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                  <Input type="tel" value={phoneNumber} onChange={e => { setPhoneNumber(e.target.value.replace(/[^\d+\-() ]/g, '').slice(0, MAX_PHONE_LENGTH)); setVerificationFailed(false); if (phoneError) setPhoneError(''); }}
                    placeholder="+1 (555) 123-4567" className={`h-12 text-base ${phoneError ? 'border-red-500' : ''}`} maxLength={MAX_PHONE_LENGTH} autoComplete="tel" />
                  {phoneError && <p className="mt-1 text-xs text-red-600">{phoneError}</p>}
                  <p className="mt-1 text-xs text-gray-400">🇺🇸 US numbers only — if outside the US, please use email verification instead</p>
                </div>
                {verificationFailed && <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4"><p className="text-sm text-red-900 font-medium">Failed to send code</p><p className="text-xs text-red-700 mt-1">Check your number or try email below</p></div>}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('email') && <button onClick={() => { setVerificationMethod('email'); setVerificationFailed(false); setFailedMethods([...failedMethods, 'phone']); }} className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">Can't receive SMS? Try email instead</button>}
                  {failedMethods.includes('email') && <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }} className="w-full text-sm py-2.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">Can't receive verification? Verify with ID instead</button>}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setVerificationMethod(''); setVerificationFailed(false); }} className="w-20 h-12 border-gray-200"><ChevronLeft className="w-5 h-5" /></Button>
                  <Button onClick={handleSendCode} disabled={sendingCode || phoneNumber.length < 10} className="flex-1 h-12 text-white font-semibold disabled:opacity-50" style={{ background: brandColor }}>
                    {sendingCode ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span> : 'Send Code'}
                  </Button>
                </div>
              </>)}

              {/* Back when no method */}
              {!verificationMethod && (
                <Button variant="outline" onClick={() => setStep(2)} className="w-20 h-12 border-gray-200"><ChevronLeft className="w-5 h-5" /></Button>
              )}
            </div>
          )}

          {/* STEP 5: Enter OTP ───────────────────────────────────────────── */}
          {step === 5 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Enter Verification Code</h2>
              <p className="text-sm text-gray-600 mb-6">
                6-digit code sent to <span className="font-medium">{verificationMethod === 'email' ? email : phoneNumber}</span>
              </p>
              <CodeInput
                value={verificationMethod === 'email' ? emailCode : smsCode}
                onChange={code => {
                  if (verificationMethod === 'email') { setEmailCode(code); latestEmailCodeRef.current = code; }
                  else { setSmsCode(code); latestSmsCodeRef.current = code; }
                }}
                onComplete={completed => {
                  if (verificationMethod === 'email') latestEmailCodeRef.current = completed;
                  else latestSmsCodeRef.current = completed;
                  if (!signing && completed.length === 6) handleSign();
                }}
                disabled={signing}
              />
              <div className="space-y-2 mb-6">
                <button onClick={handleSendCode} disabled={resendCooldown > 0 || sendingCode}
                  className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50">
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </button>
                {verificationMethod === 'email' && !failedMethods.includes('phone') && <button onClick={() => { setVerificationMethod('phone'); setFailedMethods([...failedMethods, 'email']); setStep(3); }} className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg">Try phone verification instead</button>}
                {(verificationMethod === 'phone' || verificationMethod === 'sms') && !failedMethods.includes('email') && <button onClick={() => { setVerificationMethod('email'); setFailedMethods([...failedMethods, 'phone']); setStep(3); }} className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg">Try email verification instead</button>}
                <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }} className="w-full text-sm py-2.5 text-orange-600 hover:bg-orange-50 rounded-lg">Cannot receive code? Verify with ID</button>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="w-20 h-12 border-gray-200"><ChevronLeft className="w-5 h-5" /></Button>
                <Button onClick={handleSign}
                  disabled={signing || (verificationMethod === 'email' ? emailCode.length !== 6 : smsCode.length !== 6)}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50">
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : 'Sign Waiver'}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 6: ID Verification ─────────────────────────────────────── */}
          {step === 6 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-bold text-amber-900">ID Verification Required</h2>
              </div>
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 mb-6">
                <p className="text-sm text-gray-800 mb-3">No problem! Our staff will verify your identity when you arrive.</p>
                <p className="text-sm text-gray-800 mb-3"><strong>Staff may take a photo of your government-issued ID.</strong> This serves as verification for the signed waiver.</p>
                <div className="flex items-start gap-2 pt-2 border-t border-amber-200">
                  <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-amber-900">Bring a valid ID (driver license, passport, or state ID)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(skippedVerification ? 3 : 5)} className="w-20 h-12 border-gray-200"><ChevronLeft className="w-5 h-5" /></Button>
                <Button onClick={handleIdVerificationSign} disabled={signing}
                  className="flex-1 h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50">
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : 'I Understand & Sign'}
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
